'use client';

import Image from '@/components/Image';
import { colors } from '@/theme/zeelool.theme';
import { set16ToRgba } from '@/utils/tools';
import { Slider } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface IntroData {
  title: string;
  description: string;
  leg_img?: string;
  frame_img?: string;
  image?: string;
  secondary_title: string;
  secondary_description: string;
  aspect_ratio?: string;
  frame_color?: {
    isSolid: boolean;
    bgColor: string;
    bgColor1?: string;
    transparency: number;
    transparency1?: number;
  };
}

interface IntroPartProps {
  introData: IntroData;
}

const IntroPart: React.FC<IntroPartProps> = ({ introData }) => {
  const [opacityValue, setOpacityValue] = useState<number>(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const slidePartRef = useRef<HTMLDivElement>(null);

  const showOpacityValue = opacityValue / 100;

  const chooseColor = useCallback((val: IntroData) => {
    let bgTransparent = 'transparent';
    const colorItem = val?.frame_color;
    if (colorItem) {
      if (colorItem?.isSolid) {
        bgTransparent = set16ToRgba(colorItem.bgColor, colorItem.transparency);
      } else {
        bgTransparent = `linear-gradient(${set16ToRgba(colorItem.bgColor, colorItem.transparency)}, ${set16ToRgba(colorItem.bgColor1!, colorItem.transparency1!)})`;
      }
    }

    return bgTransparent;
  }, []);

  const changeSlide = useCallback(() => {
    const targetTime = 1000; // 目标总时间，这里是1秒
    const totalIncrements = 50; // 总共要增加的次数
    const incrementDelay = Math.floor(targetTime / totalIncrements); // 每次增量之间的延迟时间

    const timerId = setInterval(() => {
      setOpacityValue((prev) => {
        if (prev >= totalIncrements) {
          clearInterval(timerId); // 清除计时器，停止递增
          return prev;
        }
        return prev + 1;
      });
    }, incrementDelay);
  }, []);

  const observeVideos = useCallback(() => {
    setTimeout(() => {
      if (introData.leg_img && introData.frame_img && slidePartRef.current) {
        const options = {
          threshold: 0.8,
        };

        observerRef.current = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // 当前slide已经进入视口区域
              changeSlide();
              observerRef.current?.unobserve(entry.target);
            }
          });
        }, options);

        // 将所有的元素添加到IntersectionObserver中
        observerRef.current.observe(slidePartRef.current);
      }
    }, 2000);
  }, [introData.leg_img, introData.frame_img, changeSlide]);

  useEffect(() => {
    observeVideos();

    // 清理函数
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [observeVideos]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setOpacityValue(newValue as number);
  };

  return (
    <div className="w-full h-full md:px-[140px] 3xl:px-[200px] box-border py-40 px-14  md:my-[104px] 3xl:my-[120px]">
      <div className="text-center mb-40 md:mb-[104px]">
        <h2 className="text-[24px] md:text-[36px] 3xl:text-[48px] font-bb mb-[16px]">
          {introData.title}
        </h2>
        <h5 className="text-[13px] md:text-[14px] 3xl:text-[16px] px-12">
          {introData.description}
        </h5>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-20">
        {introData.leg_img && introData.frame_img ? (
          <div className="intro-frame-box">
            <div className="relative w-[587px] mx-auto mb-[15px]">
              <img
                src={introData.leg_img}
                className="absolute left-0 top-0 block w-full h-auto z-[8]"
                alt=""
              />
              <div
                className="aspect-[2/1] relative z-[9] w-full h-auto"
                style={{
                  background: chooseColor(introData),
                  opacity: showOpacityValue,
                }}
              />
              <img
                src={introData.frame_img}
                className="absolute left-0 top-0 block w-full h-auto z-[10]"
                alt=""
              />
            </div>

            <div ref={slidePartRef} className="flex justify-center items-center w-full">
              <Image
                src="/activity/left_sun.png"
                alt=""
                className="w-[22px] h-auto mx-[16px]"
                width={20}
                height={20}
              />
              <Slider
                value={opacityValue}
                onChange={handleSliderChange}
                min={0}
                max={50}
                sx={{
                  width: '124px',
                  '& .MuiSlider-rail': {
                    height: '6px',
                    backgroundColor: '#EEEEEE',
                  },
                  '& .MuiSlider-track': {
                    height: '6px',
                    backgroundColor: colors.primaryPurple,
                    border: 'none',
                  },
                  '& .MuiSlider-thumb': {
                    width: '20px',
                    height: '20px',
                    borderWidth: '3px',
                    borderColor: colors.primaryPurple,
                    backgroundColor: '#fff',
                    '&:hover': {
                      boxShadow: 'none',
                    },
                    '&.Mui-focusVisible': {
                      boxShadow: 'none',
                    },
                  },
                }}
              />
              <Image
                src="/activity/right_sun.png"
                alt=""
                className="w-[22px] h-auto mx-[16px]"
                width={20}
                height={20}
              />
            </div>
          </div>
        ) : (
          <Image
            className="w-[720px] h-auto aspect-[720/405] rounded-[12px] "
            src={introData.image || ''}
            alt={introData.secondary_title}
            style={introData.aspect_ratio ? { aspectRatio: introData.aspect_ratio } : {}}
            width={720}
            height={405}
          />
        )}

        <div className="md:w-[624px]">
          <h3 className="text-[24px] md:text-[40px] font-bb mb-[16px] text-center md:text-left">
            {introData.secondary_title}
          </h3>
          <h5
            className="text-[13px] md:text-[16px] leading-[20px] px-12 md:px-0 text-center md:text-left"
            dangerouslySetInnerHTML={{ __html: introData.secondary_description }}
          />
        </div>
      </div>
    </div>
  );
};

export default IntroPart;
